import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import { canManageCourtPricing } from "../lib/access";
import { ApiError } from "../lib/api/client";
import {
  calculateCourtPrice,
  createPriceRule,
  deletePriceRule,
  listCourts,
  listOperatingHours,
  listPriceRules,
  setOperatingHours,
  updatePriceRule,
} from "../lib/api/endpoints";
import { money } from "../lib/format";
import { validateOperatingHours } from "../lib/pricingValidation";
import { useSession } from "../lib/session/SessionContext";
import { C, fieldStyle } from "../theme";
import type { CalculatedCourtPriceDto, CourtDto, CreatePriceRuleRequest, OperatingHourRequest, PriceRuleDto, WeekDay } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { Label } from "./Label";
import { PriceRuleForm } from "./PriceRuleForm";
import { DateTimePicker, TimePicker } from "./DateTimePicker";

const DAYS: Array<{ value: WeekDay; label: string; short: string }> = [
  { value: 0, label: "Domingo", short: "Dom" },
  { value: 1, label: "Segunda-feira", short: "Seg" },
  { value: 2, label: "Terça-feira", short: "Ter" },
  { value: 3, label: "Quarta-feira", short: "Qua" },
  { value: 4, label: "Quinta-feira", short: "Qui" },
  { value: 5, label: "Sexta-feira", short: "Sex" },
  { value: 6, label: "Sábado", short: "Sáb" },
];
const PRICE_RULE_PAGE_SIZE = 20;

function emptyWeek(): OperatingHourRequest[] {
  return DAYS.map(day => ({ dayOfWeek: day.value, openTime: null, closeTime: null, isClosed: true }));
}

function normalizeWeek(items: Array<{ dayOfWeek: WeekDay; openTime: string | null; closeTime: string | null; isClosed: boolean }>): OperatingHourRequest[] {
  return DAYS.map(day => {
    const value = items.find(item => item.dayOfWeek === day.value);
    return value
      ? { dayOfWeek: day.value, openTime: value.openTime, closeTime: value.closeTime, isClosed: value.isClosed }
      : { dayOfWeek: day.value, openTime: null, closeTime: null, isClosed: true };
  });
}

function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function inputTime(value: string | null): string {
  return value?.slice(0, 5) ?? "";
}

function apiTime(value: string): string {
  return value.length === 5 ? `${value}:00` : value;
}

function priceRulePayload(rule: PriceRuleDto, isActive = rule.isActive): CreatePriceRuleRequest {
  return {
    courtId: rule.courtId,
    name: rule.name,
    startTime: rule.startTime,
    endTime: rule.endTime,
    hourlyRate: rule.hourlyRate,
    priority: rule.priority,
    isActive,
    days: rule.days,
  };
}

function durationLabel(startTime: string, endTime: string): string {
  const minutes = Math.max(0, Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours ? `${hours}h` : ""}${rest ? String(rest).padStart(2, "0") : ""}` || "0min";
}

export function PricingSettingsTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageCourtPricing(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [hours, setHours] = useState<OperatingHourRequest[]>(emptyWeek);
  const [courts, setCourts] = useState<CourtDto[]>([]);
  const [rules, setRules] = useState<PriceRuleDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingHours, setSavingHours] = useState(false);
  const [formRule, setFormRule] = useState<PriceRuleDto | null | undefined>(undefined);
  const [submittingRule, setSubmittingRule] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PriceRuleDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [courtFilter, setCourtFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalRules, setTotalRules] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadError(null);
    setRules(null);
    const active = activeFilter === "all" ? undefined : activeFilter === "active";
    Promise.all([
      listOperatingHours(token),
      listCourts(token, { page: 1, pageSize: 100, isActive: true }),
      listPriceRules(token, { page, pageSize: PRICE_RULE_PAGE_SIZE, courtId: courtFilter || undefined, isActive: active }),
    ]).then(([schedule, courtResult, ruleResult]) => {
      if (cancelled) return;
      setHours(normalizeWeek(schedule));
      setCourts(courtResult.items);
      setRules(ruleResult.items);
      setTotalRules(ruleResult.totalCount);
    }).catch(error => {
      if (cancelled) return;
      setRules([]);
      setLoadError(apiErrorMessage(error, "Falha ao carregar horários e preços"));
    });
    return () => { cancelled = true; };
  }, [token, courtFilter, activeFilter, page, reloadKey]);

  const courtNames = useMemo(() => new Map(courts.map(court => [court.id, court.name])), [courts]);

  const updateHour = (day: WeekDay, update: Partial<OperatingHourRequest>) => {
    setHours(current => current.map(item => item.dayOfWeek === day ? { ...item, ...update } : item));
    setHoursError(null);
  };

  const toggleClosed = (day: OperatingHourRequest) => {
    updateHour(day.dayOfWeek, day.isClosed
      ? { isClosed: false, openTime: "06:00:00", closeTime: "23:00:00" }
      : { isClosed: true, openTime: null, closeTime: null });
  };

  const saveHours = async () => {
    if (!token || mutationRef.current) return;
    const normalized = hours.map(day => ({ ...day, openTime: day.isClosed ? null : apiTime(day.openTime ?? ""), closeTime: day.isClosed ? null : apiTime(day.closeTime ?? "") }));
    const validationErrors = validateOperatingHours(normalized);
    if (validationErrors.length) {
      setHoursError(validationErrors[0]!);
      return;
    }
    mutationRef.current = true;
    setSavingHours(true);
    setHoursError(null);
    setActionError(null);
    setSuccess(null);
    try {
      const saved = await setOperatingHours(token, { days: normalized });
      setHours(normalizeWeek(saved.length ? saved : normalized));
      setSuccess("Horários atualizados com sucesso.");
    } catch (error) {
      setHoursError(apiErrorMessage(error, "Falha ao atualizar horários"));
    } finally {
      mutationRef.current = false;
      setSavingHours(false);
    }
  };

  const openCreate = () => {
    setFormError(null);
    setActionError(null);
    setSuccess(null);
    setFormRule(null);
  };

  const submitRule = async (payload: CreatePriceRuleRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmittingRule(true);
    setFormError(null);
    try {
      const saved = formRule ? await updatePriceRule(token, formRule.id, payload) : await createPriceRule(token, payload);
      setRules(current => formRule
        ? (current ?? []).map(rule => rule.id === saved.id ? saved : rule)
        : [saved, ...(current ?? [])]);
      if (!formRule) setTotalRules(current => current + 1);
      setFormRule(undefined);
      setSuccess(formRule ? "Regra atualizada com sucesso." : "Regra criada com sucesso.");
      setReloadKey(key => key + 1);
    } catch (error) {
      setFormError(apiErrorMessage(error, "Falha ao salvar regra de preço"));
    } finally {
      mutationRef.current = false;
      setSubmittingRule(false);
    }
  };

  const toggleRule = async (rule: PriceRuleDto) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setActionError(null);
    setSuccess(null);
    try {
      const saved = await updatePriceRule(token, rule.id, priceRulePayload(rule, !rule.isActive));
      setRules(current => (current ?? []).map(item => item.id === rule.id ? saved : item));
      setSuccess(`Regra ${saved.isActive ? "ativada" : "desativada"} com sucesso.`);
      setReloadKey(key => key + 1);
    } catch (error) {
      setActionError(apiErrorMessage(error, "Falha ao alterar o status da regra"));
    } finally {
      mutationRef.current = false;
    }
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete || mutationRef.current) return;
    const rule = pendingDelete;
    mutationRef.current = true;
    setDeleting(true);
    setActionError(null);
    setSuccess(null);
    try {
      await deletePriceRule(token, rule.id);
      setRules(current => (current ?? []).filter(item => item.id !== rule.id));
      setTotalRules(current => Math.max(0, current - 1));
      setPendingDelete(null);
      setSuccess("Regra excluída com sucesso.");
      setReloadKey(key => key + 1);
    } catch (error) {
      setActionError(apiErrorMessage(error, "Falha ao excluir regra de preço"));
      setPendingDelete(null);
    } finally {
      mutationRef.current = false;
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
      {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError}<Button variant="ghost" onClick={() => setReloadKey(value => value + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button></div>}

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div><Label>Horários de funcionamento</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Horário local do CT, sem conversão para UTC.</div></div>
          {!canManage && <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}
        </div>
        <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
          {hours.map(day => {
            const meta = DAYS[day.dayOfWeek]!;
            return (
              <div key={day.dayOfWeek} style={{ display: "grid", gridTemplateColumns: "minmax(120px,1fr) minmax(115px,0.7fr) minmax(115px,0.7fr) minmax(110px,0.7fr)", gap: 10, alignItems: "center", borderBottom: `1px solid ${C.faint}`, padding: "8px 0" }}>
                <b style={{ color: C.ink, fontSize: 12.5 }}>{meta.label}</b>
                <TimePicker ariaLabel={`Abertura de ${meta.label.toLowerCase()}`} value={inputTime(day.openTime)} disabled={!canManage || day.isClosed} onChange={value => updateHour(day.dayOfWeek, { openTime: apiTime(value) })} />
                <TimePicker ariaLabel={`Fechamento de ${meta.label.toLowerCase()}`} value={inputTime(day.closeTime)} disabled={!canManage || day.isClosed} onChange={value => updateHour(day.dayOfWeek, { closeTime: apiTime(value) })} />
                <label style={{ display: "flex", alignItems: "center", gap: 7, color: C.gray, fontSize: 12 }}>
                  <input type="checkbox" aria-label={`Fechado na ${meta.label.toLowerCase()}`} checked={day.isClosed} disabled={!canManage} onChange={() => toggleClosed(day)} /> Fechado
                </label>
              </div>
            );
          })}
        </div>
        {hoursError && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{hoursError}</div>}
        {canManage && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}><Button onClick={saveHours} disabled={savingHours}>{savingHours ? "Salvando..." : "Salvar horários"}</Button></div>}
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div><Label>Tabela de preços</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Maior prioridade vence; regras específicas da quadra precedem as globais.</div></div>
          {canManage && <Button onClick={openCreate}>+ Nova regra</Button>}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          <select aria-label="Filtrar regras por quadra" value={courtFilter} onChange={event => { setCourtFilter(event.target.value); setPage(1); }} style={fieldStyle}>
            <option value="">Todas as quadras</option>
            {courts.map(court => <option key={court.id} value={court.id}>{court.name}</option>)}
          </select>
          <select aria-label="Filtrar regras por status" value={activeFilter} onChange={event => { setActiveFilter(event.target.value); setPage(1); }} style={fieldStyle}>
            <option value="all">Todos os status</option><option value="active">Ativas</option><option value="inactive">Inativas</option>
          </select>
        </div>
        {courts.length > 0 && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>{courts.map(court => <span key={court.id} style={{ color: C.gray, background: C.bg, borderRadius: 14, padding: "5px 9px", fontSize: 11.5 }}>{court.name} · padrão {money(court.defaultHourlyRate)}</span>)}</div>}
        {actionError && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 12 }}>{actionError}</div>}
        {rules === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando regras...</div>}
        {rules?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhuma regra de preço encontrada.</div>}
        {rules && rules.length > 0 && (
          <div style={{ display: "grid", gap: 9, marginTop: 14 }}>
            {rules.map(rule => (
              <div key={rule.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, opacity: rule.isActive ? 1 : 0.62 }}>
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><b style={{ color: C.ink, fontSize: 13 }}>{rule.name}</b><span style={{ color: rule.isActive ? C.ink : C.light, background: rule.isActive ? C.limeSoft : C.bg, borderRadius: 12, padding: "3px 7px", fontSize: 10.5 }}>{rule.isActive ? "Ativa" : "Inativa"}</span></div>
                  <div style={{ color: C.gray, fontSize: 11.5, marginTop: 4 }}>{rule.courtId ? courtNames.get(rule.courtId) ?? "Quadra específica" : "Global"} · prioridade {rule.priority} · {rule.days.map(day => DAYS[day]!.short).join(", ")}</div>
                  <div style={{ color: C.ink, fontSize: 12.5, marginTop: 4 }}>{inputTime(rule.startTime)}–{inputTime(rule.endTime)} · <b>{money(rule.hourlyRate)}/h</b></div>
                </div>
                {canManage && <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                  <Button variant="ghost" ariaLabel={`${rule.isActive ? "Desativar" : "Ativar"} ${rule.name}`} title={`${rule.isActive ? "Desativar" : "Ativar"} ${rule.name}`} onClick={() => toggleRule(rule)} style={{ textDecoration: "none", padding: 5 }}><Power size={16} /></Button>
                  <Button variant="ghost" ariaLabel={`Editar ${rule.name}`} title={`Editar ${rule.name}`} onClick={() => { setFormError(null); setFormRule(rule); }} style={{ textDecoration: "none", padding: 5 }}><Pencil size={16} /></Button>
                  <Button variant="ghost" ariaLabel={`Excluir ${rule.name}`} title={`Excluir ${rule.name}`} onClick={() => setPendingDelete(rule)} style={{ textDecoration: "none", padding: 5, color: C.red }}><Trash2 size={16} /></Button>
                </div>}
              </div>
            ))}
          </div>
        )}
        {totalRules > PRICE_RULE_PAGE_SIZE && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 14 }}>
            <span style={{ color: C.light, fontSize: 11.5 }}>Página {page} de {Math.ceil(totalRules / PRICE_RULE_PAGE_SIZE)}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="outline" disabled={page === 1} onClick={() => setPage(current => Math.max(1, current - 1))}>Anterior</Button>
              <Button variant="outline" disabled={page * PRICE_RULE_PAGE_SIZE >= totalRules} onClick={() => setPage(current => current + 1)}>Próxima</Button>
            </div>
          </div>
        )}
      </Card>

      <PriceCalculator courts={courts} token={token} />

      {formRule !== undefined && <PriceRuleForm courts={courts} initialRule={formRule} submitting={submittingRule} submitError={formError} onSubmit={submitRule} onCancel={() => !submittingRule && setFormRule(undefined)} />}
      {pendingDelete && <ConfirmDialog title="Excluir regra de preço" confirmLabel="Excluir regra" busy={deleting} busyLabel="Excluindo..." onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)}>Excluir <b>{pendingDelete.name}</b>? A regra deixará de participar de novos cálculos.</ConfirmDialog>}
    </div>
  );
}

function PriceCalculator({ courts, token }: { courts: CourtDto[]; token: string }) {
  const [courtId, setCourtId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [result, setResult] = useState<CalculatedCourtPriceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);

  const calculate = async () => {
    setResult(null);
    if (!courtId || !startTime || !endTime || startTime >= endTime) {
      setError("Selecione a quadra e informe um intervalo válido.");
      return;
    }
    setCalculating(true);
    setError(null);
    try {
      const calculated = await calculateCourtPrice(token, { courtId, startTime: new Date(startTime).toISOString(), endTime: new Date(endTime).toISOString() });
      setResult(calculated);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, "Falha ao calcular o preço"));
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Card>
      <Label>Simulador de preço</Label>
      <div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Confirme o valor que será aplicado antes de criar uma reserva.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10, marginTop: 14, alignItems: "end" }}>
        <div><label htmlFor="calculator-court" style={{ display: "block", color: C.gray, fontSize: 12, marginBottom: 4 }}>Quadra para simulação</label><select id="calculator-court" value={courtId} onChange={event => setCourtId(event.target.value)} style={{ ...fieldStyle, width: "100%" }}><option value="">Selecione</option>{courts.map(court => <option key={court.id} value={court.id}>{court.name}</option>)}</select></div>
        <div><label htmlFor="calculator-start" style={{ display: "block", color: C.gray, fontSize: 12, marginBottom: 4 }}>Início da reserva</label><DateTimePicker id="calculator-start" ariaLabel="Início da reserva" value={startTime} onChange={setStartTime} /></div>
        <div><label htmlFor="calculator-end" style={{ display: "block", color: C.gray, fontSize: 12, marginBottom: 4 }}>Fim da reserva</label><DateTimePicker id="calculator-end" ariaLabel="Fim da reserva" value={endTime} onChange={setEndTime} /></div>
        <Button onClick={calculate} disabled={calculating || courts.length === 0}>{calculating ? "Calculando..." : "Calcular preço"}</Button>
      </div>
      {error && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 12 }}>{error}</div>}
      {result && <div role="status" style={{ marginTop: 14, background: C.limeSoft, borderRadius: 12, padding: 14 }}><div style={{ color: C.light, fontSize: 11.5 }}>valor calculado para {durationLabel(result.startTime, result.endTime)}</div><b style={{ display: "block", color: C.ink, fontSize: 24, marginTop: 2 }}>{money(result.calculatedPrice)}</b><div style={{ color: C.gray, fontSize: 11.5 }}>{result.currency}</div></div>}
    </Card>
  );
}
